import { UIEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { axiosDefaultConfig } from 'constants/axios'
import {
  AdminsApi,
  AdminsJobBoardsVacanciesCreateJobVacancyRequest,
  AdminsJobBoardsVacanciesUpdateJobVacancyRequest,
  Configuration,
  LocationApi,
  LocationsGetDistrictsResponseItem,
} from 'generated/api'
import {
  categoryJobVacanciesEnum,
  collectionMethodEnum,
  wageUnitJobVacancyEnum,
} from './jobVacancyEnum'
import debounce from 'helpers/debounce'
import dropdownScrollFetch from 'helpers/dropdownScrollFetch'
import Toast from 'components/toast/Toast'

const axiosConfig = new Configuration(axiosDefaultConfig)
const locationApi = new LocationApi(axiosConfig)
const adminsApi = new AdminsApi(axiosConfig)

export const useJobVacancyForm = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { toast, toastContainer } = Toast()
  const [jobId, setJobId] = useState<string>('')
  const [areaName, setAreaName] = useState<string>('')
  const [fetchStatus, setFetchStatus] = useState<'idle' | 'loading' | 'error'>('idle')
  const [pageDistricts, setPageDistricts] = useState<number>(1)
  const [totalPageDisctrics, setTotalPageDistricts] = useState<number>(1)
  const [distictNameSearch, setDistrictNameSearch] = useState<string>('')
  const [districts, setDistricts] = useState<LocationsGetDistrictsResponseItem[]>([])
  const [jobVacancyForm, setJobVacancyForm] = useState<
    | AdminsJobBoardsVacanciesCreateJobVacancyRequest
    | AdminsJobBoardsVacanciesUpdateJobVacancyRequest
  >({
    jobName: '',
    categoryCode: categoryJobVacanciesEnum.HARVESTER,
    quota: 0,
    wage: {
      minimum: 0,
      maximum: 0,
      unit: wageUnitJobVacancyEnum.KG,
    },
    facilities: [],
    images: [],
    areaId: '',
    terrain: {
      condition: '',
      soilType: '',
      minimumTreeHigh: 6,
    },
    ffb: {
      minimumEstimation: 0,
      maximumEstimation: 0,
      collectionMethod: collectionMethodEnum.DIPIKUL,
      additionalParameter: '',
    },
    jobDescription: '',
  })

  useEffect(() => {
    fetchDistricts()
  }, [pageDistricts, distictNameSearch])

  useEffect(() => {
    if (params.jobId) {
      setJobId(params.jobId)
    }
  }, [params.jobId])

  useEffect(() => {
    if (jobId) fetchJobVacanyDetail()
  }, [jobId])

  const fetchJobVacanyDetail = async () => {
    setFetchStatus('loading')
    const { data } = await adminsApi.adminsJobBoardsVacanciesGetJobVacancyDetail(jobId)
    setJobVacancyForm({
      jobName: data.jobName,
      categoryCode: data.category.code,
      quota: data.quota,
      wage: {
        minimum: data.wage.minimum,
        maximum: data.wage.maximum,
        unit: data.wage.unit,
      },
      facilities: data.facilities,
      images: data.images,
      areaId: data.location.areaId,
      terrain: {
        condition: data.terrain.condition,
        soilType: data.terrain.soilType,
        minimumTreeHigh: data.terrain.minimumTreeHigh,
      },
      ffb: {
        minimumEstimation: data.ffb.minimumEstimation,
        maximumEstimation: data.ffb.maximumEstimation,
        collectionMethod: data.ffb.ffbCollectionMethod,
        additionalParameter: data.ffb.additionalParameter,
      },
      jobDescription: data.jobDescription,
    })
    setAreaName(data.location.areaName)
    setFetchStatus('idle')
  }

  const fetchDistricts = async () => {
    const response = await locationApi.locationsGetDistricts(
      distictNameSearch ? distictNameSearch : undefined,
      undefined,
      undefined,
      pageDistricts,
      10,
    )
    setDistricts(districts.concat(response.data.items))
    setTotalPageDistricts(response.data.pagination.pages)
  }

  const fetchDistrictsByName = debounce((value: string) => {
    setDistricts([])
    setPageDistricts(1)
    setDistrictNameSearch(value)
  }, 500)

  const fetchDistrictsByPage = (event: UIEvent<HTMLDivElement>) => {
    dropdownScrollFetch(event, pageDistricts, totalPageDisctrics, () =>
      setPageDistricts(pageDistricts + 1),
    )
  }

  const createJobVacancy = async (values: AdminsJobBoardsVacanciesCreateJobVacancyRequest) => {
    try {
      setFetchStatus('loading')
      await adminsApi.adminsJobBoardsVacanciesCreateJobVacancy({
        jobName: values.jobName,
        categoryCode: values.categoryCode,
        quota: values.quota,
        wage: {
          minimum: values.wage.minimum,
          maximum: values.wage.maximum,
          unit: values.wage.unit,
        },
        facilities: values.facilities,
        images: values.images,
        areaId: values.areaId,
        terrain: {
          condition: values.terrain.condition,
          soilType: values.terrain.soilType,
          minimumTreeHigh: values.terrain.minimumTreeHigh,
        },
        ffb: {
          minimumEstimation: values.ffb.minimumEstimation,
          maximumEstimation: values.ffb.maximumEstimation,
          collectionMethod: values.ffb.collectionMethod,
          additionalParameter: values.ffb.additionalParameter,
        },
        jobDescription: values.jobDescription,
      })
      setFetchStatus('idle')
      navigate('/job-vacancy', {
        state: {
          jobVacancyForm: 'success-create-job-vacancy',
        },
      })
    } catch (error) {
      setFetchStatus('idle')
      await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
    }
  }

  const updateJobVacancy = async (values: AdminsJobBoardsVacanciesUpdateJobVacancyRequest) => {
    try {
      setFetchStatus('loading')
      await adminsApi.adminsJobBoardsVacanciesUpdateJobVacancy(jobId, {
        jobName: values.jobName,
        categoryCode: values.categoryCode,
        quota: values.quota,
        wage: {
          minimum: values.wage.minimum,
          maximum: values.wage.maximum,
          unit: values.wage.unit,
        },
        facilities: values.facilities,
        images: values.images,
        areaId: values.areaId,
        terrain: {
          condition: values.terrain.condition,
          soilType: values.terrain.soilType,
          minimumTreeHigh: values.terrain.minimumTreeHigh,
        },
        ffb: {
          minimumEstimation: values.ffb.minimumEstimation,
          maximumEstimation: values.ffb.maximumEstimation,
          collectionMethod: values.ffb.collectionMethod,
          additionalParameter: values.ffb.additionalParameter,
        },
        jobDescription: values.jobDescription,
      })
      setFetchStatus('idle')
      navigate('/job-vacancy', {
        state: {
          jobVacancyForm: 'success-update-job-vacancy',
        },
      })
    } catch (error) {
      setFetchStatus('idle')
      await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
    }
  }

  const navigateToJobVacancies = () => {
    navigate('/job-vacancy')
  }

  return {
    jobId,
    areaName,
    toastContainer,
    fetchStatus,
    jobVacancyForm,
    fetchDistrictsByName,
    fetchDistrictsByPage,
    districts,
    navigateToJobVacancies,
    createJobVacancy,
    updateJobVacancy,
    setAreaName,
  }
}
